import { ObjectType } from '@quromedical/fhir-common';
import { CreateMediaData } from '@quromedical/models';
import { Row } from 'components/base';
import { CrudForm, SubmissionHandler } from 'components/form';
import { Field, FileResult } from 'components/types';
import { Card, FileKeyProgressMap, Skeleton, SkeletonProvider, Text } from 'design-system';
import { useFileUpload } from 'hooks/useFileUpload';
import { MediaApi } from 'integration/aggregate';
import React, { useCallback, useState } from 'react';
import { strings } from 'strings';
import * as yup from 'yup';

const mediaApi = new MediaApi();

interface NoteUpdateFormProps {
  id: string;
  onEditSubmit: (subjective: string, media: CreateMediaData[]) => Promise<void>;
  toggleEditForm: () => void;
}

export interface UpdateNoteFormData {
  subjective: string;
  media: FileResult[];
}

export const updateFormSchema = yup.object<ObjectType<UpdateNoteFormData>>({
  subjective: yup.string(),
});

export const NoteUpdateForm: React.FC<NoteUpdateFormProps> = ({
  id,
  onEditSubmit,
  toggleEditForm,
}) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [showSuccess, setSuccess] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  // we need to keep track of the last submission as a key to refresh the form after submission
  const [lastSubmitted, setLastSubmitted] = useState(Date.now());

  const [formState, setFormState] = useState({});
  const handleReset = useCallback(() => {
    setFormState({});
    setError(undefined);
    setLastSubmitted(Date.now());
  }, []);

  const upload = useFileUpload(data => {
    return mediaApi.createUploadUrl(id, data);
  }, id);

  const onChange = useCallback(
    (state: Partial<UpdateNoteFormData>) => {
      setFormState(state);
      upload.onFilesChange(state);
    },
    [setFormState, upload]
  );

  const handleSubmit = useCallback<SubmissionHandler<UpdateNoteFormData>>(
    async (data: UpdateNoteFormData) => {
      try {
        setSubmitting(true);
        await onEditSubmit(data.subjective, upload.media);
        handleReset();
        setSuccess(true);
        setError(undefined);
        return {};
      } catch (error) {
        setError(strings.SOAPNoteSubmissionErrorMessage);
        setSuccess(false);
        return { error };
      } finally {
        toggleEditForm();
        setSubmitting(false);
      }
    },
    [handleReset, onEditSubmit, toggleEditForm, upload.media]
  );

  interface UpdateFieldProps {
    progress?: FileKeyProgressMap;
    uploadInProgress?: boolean;
  }

  const createFields = ({
    progress = {},
    uploadInProgress = false,
  }: UpdateFieldProps): Field<UpdateNoteFormData>[] => {
    return [
      {
        icon: 'text-s',
        subfields: [
          {
            key: 'subjective',
            type: 'text-box',
            label: '',
            placeholder: strings.SOAPNoteSubjectiveCommentsPlaceholder,
            multiline: true,
          },
        ],
      },
      {
        subfields: [
          {
            key: 'media',
            type: 'file-picker-dialog',
            label: strings.FormLabelAttachments,
            buttonText: strings.ButtonTextAddAttachment,
            progress,
            uploadInProgress,
          },
        ],
      },
    ];
  };

  const fields = createFields({
    progress: upload.progress,
    uploadInProgress: !upload.uploadsComplete,
  });

  return (
    <SkeletonProvider loading={isSubmitting}>
      <Card paddingHorizontal={0} paddingVertical={0} unsetZIndex border={false}>
        <Skeleton>
          <CrudForm
            key={lastSubmitted}
            title={strings.SOAPNoteAdditionalNoteTextTitle}
            disabled={isSubmitting}
            hasCard={false}
            fields={fields}
            initialValues={formState}
            onChangeDebounceTime={0}
            onChange={onChange}
            validationSchema={updateFormSchema}
            onSubmit={handleSubmit}
            includeIcons
          />
        </Skeleton>
        <Row>
          {error ? <Text color="status-critical">{error}</Text> : null}
          {showSuccess ? (
            <Text color="status-success">{strings.SOAPNoteEditSubmissionSuccessMessage}</Text>
          ) : null}
        </Row>
      </Card>
    </SkeletonProvider>
  );
};
